/**
 * Wave A — `<RequirePermission>`.
 *
 * Conditionally renders `children` based on a permission check. While the
 * permissions cache is warming up, renders a small inline spinner; on denial,
 * renders `fallback` (defaults to `null`).
 *
 * Use for fine-grained UI gating (buttons, table actions, sidebar items).
 * For full-page route guards, prefer extending `RoleGuard` with the
 * `permissions` prop or, post-cutover, using this component at the route
 * `<Route element>` level.
 *
 * @module shared/components/RequirePermission
 */
import React from 'react';
import { Spin } from 'antd';
import { usePermissions } from '../hooks/usePermissions';

export interface RequirePermissionProps {
  /** Dotted permission id, e.g. `tenant.members.update`. */
  permission: string;
  /** Resource id to scope the check; omit for platform-scoped permissions. */
  resourceId?: string;
  /** Rendered when the user is denied. Defaults to `null`. */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

export const RequirePermission: React.FC<RequirePermissionProps> = ({
  permission,
  resourceId,
  fallback = null,
  children,
}) => {
  const { has, loading } = usePermissions();

  if (loading) {
    return (
      <span
        role="status"
        aria-live="polite"
        style={{ display: 'inline-flex', alignItems: 'center' }}
      >
        <Spin size="small" />
      </span>
    );
  }

  return has(permission, resourceId) ? <>{children}</> : <>{fallback}</>;
};

export default RequirePermission;
